import { computed, ref, watchEffect, type ComputedRef, type Ref } from 'vue'
import { isObject, StorageSerializers, useLocalStorage } from '@vueuse/core'

/** Unchecked by default: remembering is an active choice about storing data on the device. */
export const REMEMBER_ME_DEFAULT = false
export const REMEMBERED_EMAIL_KEY = 'remembered_login_email'
// Mirrors the server's absolute refresh limit for a login (30 days), which is not exposed to the client.
export const REMEMBERED_EMAIL_TTL_DAYS = 30

export interface RememberedLogin {
  email: string
  /** Milliseconds since the epoch when the entry was written. */
  savedAt: number
}

export interface SessionParams {
  endSessionOnClose?: 'true'
}

const REMEMBERED_EMAIL_TTL_MS = REMEMBERED_EMAIL_TTL_DAYS * 24 * 60 * 60 * 1000

// Anything may sit under the key. A value that is not even JSON is read as an
// invalid entry so that it is removed like any other malformed entry.
const tolerantObjectSerializer = {
  read: (raw: string): unknown => {
    try {
      return StorageSerializers.object.read(raw)
    } catch {
      return {}
    }
  },
  write: (value: unknown): string => StorageSerializers.object.write(value),
}

// Module-level so every caller reads and writes the same entry.
const storedLogin = useLocalStorage<unknown>(REMEMBERED_EMAIL_KEY, null, { serializer: tolerantObjectSerializer })

function isRememberedLogin(value: unknown): value is RememberedLogin {
  return (
    isObject(value) &&
    'email' in value &&
    typeof value.email === 'string' &&
    'savedAt' in value &&
    typeof value.savedAt === 'number'
  )
}

/** The stored entry when it is well-formed and younger than the limit, otherwise `null`. */
function readValidEntry(value: unknown): RememberedLogin | null {
  if (!isRememberedLogin(value)) return null
  const age = Date.now() - value.savedAt
  if (age < 0 || age > REMEMBERED_EMAIL_TTL_MS) return null
  return value
}

/**
 * The "remember me" choice on the login page: the stored address for the next visit and the
 * session parameters both the OAuth and the one-time-code path send along.
 */
export function useRememberedLogin(): {
  rememberedEmail: ComputedRef<string | null>
  rememberMe: Ref<boolean>
  sessionParams: ComputedRef<SessionParams>
  commit: (email: string) => void
  forget: () => void
} {
  const rememberMe = ref(REMEMBER_ME_DEFAULT)

  watchEffect(() => {
    if (storedLogin.value != null && readValidEntry(storedLogin.value) == null) storedLogin.value = null
  })

  const rememberedEmail = computed(() => readValidEntry(storedLogin.value)?.email ?? null)

  // An unchecked box means a session that ends with the browser; a checked box means today's persistent login.
  const sessionParams = computed<SessionParams>(() => (rememberMe.value ? {} : { endSessionOnClose: 'true' }))

  function commit(email: string) {
    // Every login with the box checked restarts the 30 days, even for an unchanged address.
    storedLogin.value = rememberMe.value ? { email, savedAt: Date.now() } : null
  }

  function forget() {
    storedLogin.value = null
  }

  return { rememberedEmail, rememberMe, sessionParams, commit, forget }
}
